import { Download, FilterX, X } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import type { Option } from '@/constants/institute';
import { cn } from '@/lib/utils';
import type { ParentTaskPriority, ParentWorkType } from '../../types/parent';
import { TASK_PRIORITY_LABEL, WORK_TYPE_LABEL, formatDueDay } from '../../utils';

/** The four views of the list a parent switches between. */
export type WorkStatusFilter = 'ALL' | 'PENDING' | 'COMPLETED' | 'OVERDUE';

/** How far ahead the list reaches. */
export type WorkDueFilter = 'all' | 'overdue' | 'week' | 'month';

export interface WorkFilterState {
  status: WorkStatusFilter;
  /** A subject name, or `all`. Names rather than ids, as the mock has no ids. */
  subject: string;
  type: ParentWorkType | 'all';
  priority: ParentTaskPriority | 'all';
  due: WorkDueFilter;
  /**
   * One day, picked off the rail's calendar. Kept apart from `due` because it
   * answers a different question — "what is due on the 12th", not "what is due
   * soon" — and because clicking a day should not silently undo the range the
   * parent chose from the dropdown.
   */
  dueOn: string | null;
}

/** Nothing chosen — what "clear the filters" puts back. */
export const EMPTY_FILTERS: WorkFilterState = {
  status: 'ALL',
  subject: 'all',
  type: 'all',
  priority: 'all',
  due: 'all',
  dueOn: null,
};

export function isFiltered(filters: WorkFilterState): boolean {
  return (
    filters.status !== 'ALL' ||
    filters.subject !== 'all' ||
    filters.type !== 'all' ||
    filters.priority !== 'all' ||
    filters.due !== 'all' ||
    filters.dueOn !== null
  );
}

/** Exported, with `DUE_OPTIONS`, so the page can check values read from the address. */
export const STATUS_CHIPS: { value: WorkStatusFilter; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'OVERDUE', label: 'Overdue' },
];

export const DUE_OPTIONS: Option[] = [
  { value: 'all', label: 'Any date' },
  { value: 'overdue', label: 'Past the due date' },
  { value: 'week', label: 'Due this week' },
  { value: 'month', label: 'Due this month' },
];

/** Every kind there is, for a caller that has not said which ones it holds. */
const ALL_TYPES = Object.keys(WORK_TYPE_LABEL) as ParentWorkType[];

function typeOptions(types: ParentWorkType[]): Option[] {
  return [
    { value: 'all', label: 'All types' },
    ...types.map((type) => ({ value: type, label: WORK_TYPE_LABEL[type] })),
  ];
}

const PRIORITY_OPTIONS: Option[] = [
  { value: 'all', label: 'All priorities' },
  ...(Object.keys(TASK_PRIORITY_LABEL) as ParentTaskPriority[]).map((priority) => ({
    value: priority,
    label: TASK_PRIORITY_LABEL[priority],
  })),
];

export interface WorkToolbarProps {
  filters: WorkFilterState;
  onChange: (filters: WorkFilterState) => void;
  /** Every subject the child actually has work in — never a fixed list. */
  subjects: string[];
  /**
   * Every kind of work the child actually has, on the same terms as `subjects`:
   * a dropdown offering "Project" to a class that has been set none is three
   * choices that can only ever answer "nothing found". Omitted means all of
   * them, which is what a caller drawing from a list that holds every kind
   * wants.
   */
  types?: ParentWorkType[];
  /**
   * Which narrowing filter the middle slot carries.
   *
   * Homework is filtered by what kind of work it is; a task by how urgent the
   * school called it. One slot with two jobs rather than a fourth dropdown
   * that is empty on whichever tab does not use it.
   */
  middle: 'type' | 'priority';
  onClear: () => void;
  /** Saves what is on screen, already filtered, as a spreadsheet. */
  onDownload: () => void;
  /** Nothing to save, so the button says so rather than writing an empty file. */
  canDownload: boolean;
}

/**
 * The controls above the list: four views, three narrowing filters, one export.
 *
 * The chips and the dropdowns do different jobs on purpose. A parent asking
 * "what is still to do?" is switching view, and that is one tap; asking "what
 * is left in Maths this week?" is narrowing, and that is a choice from a list.
 * Putting all seven in dropdowns would bury the question that gets asked daily.
 */
export function WorkToolbar({
  filters,
  onChange,
  subjects,
  types = ALL_TYPES,
  middle,
  onClear,
  onDownload,
  canDownload,
}: WorkToolbarProps) {
  const subjectOptions: Option[] = [
    { value: 'all', label: 'All subjects' },
    ...subjects.map((subject) => ({ value: subject, label: subject })),
  ];

  return (
    <div className="rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 p-3.5 sm:px-4 py-3 shadow-xs flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
      <div className="flex flex-col gap-2">
        <div
          role="group"
          aria-label="Show which work"
          className="flex flex-wrap items-center gap-2"
        >
          {STATUS_CHIPS.map((chip) => {
            const isActive = filters.status === chip.value;

            return (
              <button
                key={chip.value}
                type="button"
                aria-pressed={isActive}
                onClick={() => onChange({ ...filters, status: chip.value })}
                className={cn(
                  'rounded-full px-4 py-1.5 text-xs sm:text-sm font-bold transition-all cursor-pointer',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                  isActive
                    ? 'bg-indigo-600 text-white shadow-xs'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 border border-transparent',
                )}
              >
                {chip.label}
              </button>
            );
          })}
        </div>

        {/* The day picked off the calendar */}
        {filters.dueOn && (
          <button
            type="button"
            onClick={() => onChange({ ...filters, dueOn: null })}
            className="inline-flex w-fit items-center gap-1.5 rounded-full border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950/50 px-3 py-1 text-xs font-bold text-indigo-600 dark:text-indigo-400 transition-colors hover:border-indigo-400 cursor-pointer"
          >
            Due on {formatDueDay(filters.dueOn)}
            <X className="h-3.5 w-3.5" aria-hidden />
            <span className="sr-only">Clear the chosen day</span>
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-2.5">
        <div className="w-full sm:w-44">
          <Select
            label="Subject"
            labelClassName="!text-slate-500 dark:!text-slate-400 !font-semibold"
            options={subjectOptions}
            value={filters.subject}
            onChange={(event) => onChange({ ...filters, subject: event.target.value })}
          />
        </div>

        <div className="w-full sm:w-40">
          {middle === 'priority' ? (
            <Select
              label="Priority"
              labelClassName="!text-slate-500 dark:!text-slate-400 !font-semibold"
              hideSearch
              options={PRIORITY_OPTIONS}
              value={filters.priority}
              onChange={(event) =>
                onChange({ ...filters, priority: event.target.value as ParentTaskPriority | 'all' })
              }
            />
          ) : (
            <Select
              label="Type"
              labelClassName="!text-slate-500 dark:!text-slate-400 !font-semibold"
              hideSearch
              options={typeOptions(types)}
              value={filters.type}
              onChange={(event) =>
                onChange({ ...filters, type: event.target.value as ParentWorkType | 'all' })
              }
            />
          )}
        </div>

        <div className="w-full sm:w-48">
          <Select
            label="Due date"
            labelClassName="!text-slate-500 dark:!text-slate-400 !font-semibold"
            hideSearch
            options={DUE_OPTIONS}
            value={filters.due}
            onChange={(event) =>
              onChange({ ...filters, due: event.target.value as WorkDueFilter })
            }
          />
        </div>

        {/* Clear filter button */}
        {isFiltered(filters) && (
          <Button
            variant="secondary"
            onClick={onClear}
            leftIcon={<FilterX className="h-4 w-4" aria-hidden />}
          >
            Clear
          </Button>
        )}

        <Button
          variant="primary"
          onClick={onDownload}
          disabled={!canDownload}
          title={canDownload ? undefined : 'Nothing to download with these filters'}
          leftIcon={<Download className="h-4 w-4" aria-hidden />}
          className="tracking-wide"
        >
          Download List
        </Button>
      </div>
    </div>
  );
}
